<script setup lang="ts">
import { useRuntimeConfig } from "#app";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { apiClient } = useShopwareContext();
const { isLoggedIn, logout, user } = useUser();
const loginModalController = useModal();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const { logoData } = useLogo();
</script>

<template>
  <!-- Minimal Header -->
  <header
    class="minimal-header-wrapper w-full sticky top-0 z-40 lg:z-[49] bg-white border-b border-grey-200"
  >
    <div class="container mx-auto py-4 md:py-6">
      <div class="flex flex-row items-center gap-6">
        <!-- Logo -->
        <div class="ml-0 mr-auto">
          <div class="logo md:ml-4 mr-auto lg:ml-0 lg:mr-0 order-1 lg:order-1">
            <NuxtLink to="/" :aria-label="t('header.ariaLabelHome')">
              <img
                :src="logoData?.url"
                :alt="logoData?.alt ?? t('header.ariaLabelLogo')"
                :height="logoData?.height ?? 56"
                :width="logoData?.width ?? 322"
                class="w-[140px] md:w-[200px] lg:w-[260px] xl:w-[322px] transition-all duration-300 ease-in-out"
              />
            </NuxtLink>
          </div>
        </div>

        <!-- Contact Info - Center -->
        <div class="text-start hidden lg:block ml-auto mr-auto">
          <div class="text-gray-800 font-bold text-base xl:text-md">
            {{ $t('cart.questions') }}
          </div>
          <div
            class="flex items-start justify-start gap-2 text-gray-600 text-sm xl:text-base"
          >
            <a
              :href="`tel:${$t('companyDetails.contactNumber')}`"
              class="hover:text-primary font-bold text-primary text-nowrap hover:underline"
              >{{ $t('companyDetails.contactNumber') }}</a
            >
            <span class="text-nowrap">{{ $t('companyDetails.timingsMonday') }},</span>
            <span class="text-nowrap">{{ $t('companyDetails.timingsSaturday') }}</span>
          </div>
        </div>

        <!-- Rating and Back Button -->
        <!-- Rating -->
        <LazyLayoutHeaderRatingWidget class="block right-auto relative md:top-auto md:right-auto text-center" />

        <!-- Back to Shop Button -->
        <NuxtLink
          v-if="isLoggedIn"
          class="action-button lg:btn lg:btn-secondary btn-icon-start account-button"
          aria-label="My Account"
          :to="formatLink('/account')"
        >
          <i
            class="icon-person-outline !mr-0 lg:!mr-2 !text-md"
            aria-hidden="true"
          ></i>
          <span class="my-auto hidden lg:block md:block">{{ $t('cart.myAccount') }}</span>
        </NuxtLink>
        <button
          v-else
          class="action-button lg:btn lg:btn-secondary btn-icon-start account-button"
          aria-label="My Account"
          @click="loginModalController.open"
        >
          <i
            class="icon-person-outline !mr-0 lg:!mr-2 !text-md"
            aria-hidden="true"
          ></i>
          <span class="my-auto hidden lg:block md:block">{{ $t('cart.login') }}</span>
        </button>
      </div>
    </div>
  </header>
  <SharedModal :controller="loginModalController">
      <AccountLoginForm
        @close="loginModalController.close"
        @success="loginModalController.close"
      >
      </AccountLoginForm>
    </SharedModal>
</template>
